import { useAuth } from '@/hooks/useAuth';

export interface RecentViewIdentity {
  /** The bucket to read/write: a user id, or null for a genuine visitor. */
  userId: string | null;
  /** False while the answer is not yet known — do not touch storage. */
  ready: boolean;
}

/**
 * Which recently-viewed bucket this browser belongs to (ENG-2009).
 *
 * The `:anon` bucket is only correct for a visitor with NO session. Three
 * states put `user` at null while a session exists, and treating any of them as
 * anonymous both loses the row (no signed-in screen reads that bucket) and, on
 * a shared browser, shows one visitor's share-link history to the next person
 * who signs in — the leak the per-user key exists to prevent:
 *
 *  - The auth SDK is still hydrating. `isIdentityPending` does NOT cover this:
 *    it is gated on `identityEnabled`, which is false until the SDK reports a
 *    session (`useAuth.ts`), so it reads false during the whole window. The
 *    `/share/...` routes sit outside `_app` and mount before their own
 *    auth-loading return, and `_app`'s guard defers while loading, so both
 *    sides of this feature can render inside it.
 *  - `/auth/current` is in flight — the case `isIdentityPending` does cover.
 *  - `/auth/current` errored. Pending and fetching are both false and `data` is
 *    undefined, so `isIdentityPending` is false again, permanently, for as long
 *    as that error stands.
 *
 * `isAuthenticated` ("the browser holds a session") is what separates the three
 * from a real anonymous visitor, so it is the term that decides.
 */
export function useRecentViewIdentity(): RecentViewIdentity {
  const { user, isAuthenticated, isLoading, isIdentityPending } = useAuth();

  if (isLoading) return { userId: null, ready: false };
  if (isAuthenticated && !user) return { userId: null, ready: false };
  if (isIdentityPending) return { userId: null, ready: false };

  return { userId: user?.id ?? null, ready: true };
}
